import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ContractWizardState, ContractEvent, ContractProperty, ContractPropertyTreeNode, AvailableMapping, TransformationConfig } from '../../types/contract';
import ContractNormalizationLayout from './ContractNormalizationLayout';
import NormalizationPropertiesPanel from './NormalizationPropertiesPanel';
import ContractEventsPanel from './ContractEventsPanel';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { getPlaybookAIMappingSuggestion, getPlaybookAIMappingSuggestionsBatch, PlaybookAIMappingResult } from '../../services/playbookAIMappingService';
import { fetchAllSchemaDefinitions } from '../../services/schemaDefinitionApiService';
import { useNotifications } from '../../contexts/NotificationContext';
import AIMappingProgressDialog from '../dialogs/AIMappingProgressDialog';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import axios from 'axios';
import { normalizeCodeName, toFriendlyName, isValidCodeName } from '../../utils/schemaUtils';
import { v4 as uuidv4 } from 'uuid';

const DataNormalizationTab: React.FC<{
  state: ContractWizardState;
  updateState: (updates: Partial<ContractWizardState>) => void;
  selectedEvent: ContractEvent | null;
  onEventSelect: (event: ContractEvent | null) => void;
  handleAddProperty: (eventId: string, parentProperty: ContractProperty | null) => void;
  handleUpdateProperty: (propertyId: string, updates: Partial<ContractProperty>) => void;
  handleDeleteProperty: (propertyId: string, deleteFromAllEvents?: boolean) => void;
}> = ({ state, updateState, selectedEvent, onEventSelect, handleAddProperty, handleUpdateProperty, handleDeleteProperty }) => {
  const { showNotification } = useNotifications();
  const [availableMappings, setAvailableMappings] = useState<AvailableMapping[]>([]);
  const [isLoadingMappings, setIsLoadingMappings] = useState(true);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [aiProgress, setAiProgress] = useState(0);
  const [showAIMappingProgressDialog, setShowAIMappingProgressDialog] = useState(false);
  const [aiProgressDetail, setAiProgressDetail] = useState<{
    current: number;
    total: number;
    message?: string;
  } | undefined>(undefined);
  const [aiMappingStats, setAiMappingStats] = useState<{
    total: number;
    valid: number;
    invalid: number;
    matched: number;
    unmatched: number;
  } | undefined>(undefined);
  const [aiMappingError, setAiMappingError] = useState<string | undefined>(undefined);
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const [shouldAutoMap, setShouldAutoMap] = useState(false);
  const isSmallScreen = useMediaQuery('(max-width: 1600px)');

  // Event Name, Code Name editing states
  const [isEventNameEditing, setIsEventNameEditing] = useState(false);
  const [isEventCodeNameEditing, setIsEventCodeNameEditing] = useState(false);
  const eventNameInputRef = React.useRef<HTMLInputElement>(null);
  const eventCodeNameInputRef = React.useRef<HTMLInputElement>(null);
  const [isEventCodeNameManuallyEdited, setIsEventCodeNameManuallyEdited] = useState(false);
  const [eventCodeNameError, setEventCodeNameError] = useState<string | null>(null);

  // State for event deletion confirmation
  const [showConfirmDeleteEventDialog, setShowConfirmDeleteEventDialog] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<ContractEvent | null>(null);

  // Reset manual edit flag when selected event changes
  useEffect(() => {
    setIsEventCodeNameManuallyEdited(false);
    setEventCodeNameError(null); // Clear error on event change
  }, [selectedEvent]);

  // Calculate mapping progress based on allContractProperties
  const mappingProgress = useMemo(() => {
    const total = state.allContractProperties.length;
    const mapped = state.allContractProperties.filter(prop => prop.isNormalized).length;
    const required = state.allContractProperties.filter(prop => prop.isRequired).length;
    const requiredMapped = state.allContractProperties
      .filter(prop => prop.isRequired && prop.isNormalized)
      .length;

    return {
      total,
      mapped,
      required,
      requiredMapped,
      percentage: total > 0 ? Math.round((mapped / total) * 100) : 0
    };
  }, [state.allContractProperties]);

  // Load available mappings from schema definitions
  useEffect(() => {
    const loadMappings = async () => {
      try {
        setIsLoadingMappings(true);
        const schemaDefinitions = await fetchAllSchemaDefinitions();
        const uniqueMappings = new Map<string, AvailableMapping>();

        schemaDefinitions
          .filter(schema => state.selectedSchemaIds.includes(schema.id))
          .forEach(schema => {
            schema.properties?.forEach(prop => {
              if (!uniqueMappings.has(prop.path)) {
                uniqueMappings.set(prop.path, { name: prop.name, path: prop.path, type: prop.type });
              }
            });
          });
        setAvailableMappings(Array.from(uniqueMappings.values()));
      } catch (error) {
        showNotification('error', 'Failed to load available mappings');
      } finally {
        setIsLoadingMappings(false);
      }
    };
    loadMappings();
  }, [state.selectedSchemaIds, showNotification]);

  // Update mapping progress in state
  useEffect(() => {
    updateState({
      mappingProgress: {
        total: mappingProgress.total,
        mapped: mappingProgress.mapped,
        required: mappingProgress.required,
        requiredMapped: mappingProgress.requiredMapped
      }
    });
  }, [mappingProgress, updateState]);

  // Handle AI mapping suggestion for a single property
  const handleAIMappingSuggestion = useCallback(async (propertyId: string) => {
    const property = state.allContractProperties.find(p => p.id === propertyId);
    if (!property) return;
    try {
      setIsProcessingAI(true);
      const suggestion = await getPlaybookAIMappingSuggestion(
        { name: property.name, path: property.path, type: property.dataType || 'string' },
        availableMappings.map(m => ({ name: m.name, path: m.path, type: m.type }))
      );
      if (suggestion) {
        let newTransformationConfig: TransformationConfig = {
          ...(state.normalizedProperties[property.id]?.transformationConfig || {}),
          mappingType: suggestion.isRegex ? 'regex' : 'direct',
          regexPattern: suggestion.isRegex ? suggestion.regexPattern || '' : '',
        };
        handleUpdateProperty(property.id, {
          normalizedPath: suggestion.normalizedPath,
          isNormalized: true,
          transformationType: 'mapping',
          transformationConfig: newTransformationConfig,
          matchType: 'ai',
          aiCertaintyScore: suggestion.certaintyScore,
          aiSuggested: true
        });
      }
    } catch (error) {
      console.error('Failed to get AI mapping suggestion:', error);
      showNotification('error', 'Failed to get AI mapping suggestion');
    } finally {
      setIsProcessingAI(false);
    }
  }, [state.allContractProperties, availableMappings, state.normalizedProperties, handleUpdateProperty, showNotification]);

  // Handle AI mapping suggestions for all unmapped properties
  const handleAIMappingBatch = useCallback(async () => {
    const unmappedProperties = state.allContractProperties.filter(property => {
      if (property.isNormalized && property.matchType !== 'ai') {
        return false;
      }
      return (
        !property.isNormalized ||
        (property.matchType === 'ai' &&
         (property.aiCertaintyScore === undefined || property.aiCertaintyScore < 0.5 || !property.normalizedPath))
      );
    });

    if (unmappedProperties.length === 0) {
      showNotification('info', 'All properties are already mapped or have been attempted by AI with sufficient certainty.');
      return;
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      setIsProcessingAI(true);
      setAiProgress(0);
      setAiProgressDetail(undefined);
      setAiMappingError(undefined);
      setAiMappingStats(undefined);
      setShowAIMappingProgressDialog(true);

      const result = await getPlaybookAIMappingSuggestionsBatch(
        unmappedProperties.map(p => ({ id: p.id, name: p.name, path: p.path, type: p.dataType || 'string' })),
        availableMappings.map(m => ({ name: m.name, path: m.path, type: m.type })),
        (progress: number, details?: { current: number; total: number; message?: string; suggestions?: PlaybookAIMappingResult[] }) => {
          setAiProgress(progress);

          if (details) {

            setAiProgressDetail({
              current: details.current,
              total: details.total,
              message: details.message || `${details.current}/${details.total} properties processed`
            });

            if (details.suggestions) {
              details.suggestions.forEach(suggestion => {
                const property = state.allContractProperties.find(p => p.path === suggestion.inputPath);
                if (property) {
                  if (suggestion.certaintyScore > 0.7 || !state.normalizedProperties[property.id]) {
                    let newTransformationConfig: TransformationConfig = {
                      ...(state.normalizedProperties[property.id]?.transformationConfig || {}),
                      mappingType: suggestion.isRegex ? 'regex' : 'direct',
                      regexPattern: suggestion.isRegex ? suggestion.regexPattern || '' : '',
                    };
                    handleUpdateProperty(property.id, {
                      normalizedPath: suggestion.normalizedPath,
                      isNormalized: true,
                      transformationType: 'mapping',
                      transformationConfig: newTransformationConfig,
                      matchType: 'ai',
                      aiCertaintyScore: suggestion.certaintyScore,
                      aiSuggested: true
                    });
                  }
                }
              });
            }
          } else if (typeof progress === 'number' && progress > 0 && progress < 100) {
            const current = Math.round((unmappedProperties.length * progress) / 100);
            const total = unmappedProperties.length;

            setAiProgressDetail({
              current,
              total,
              message: `${current}/${total} properties processed`
            });
          }
        },
        signal
      );

      setAiMappingStats(result.stats);

      const successCount = result.stats?.matched || 0;
      const totalCount = result.stats?.total || 0;

      if (successCount > 0) {
        showNotification('success', `AI mapping suggestions applied successfully. Mapped ${successCount} out of ${totalCount} properties.`);
      } else {
        showNotification('info', 'No properties could be mapped automatically. Try manual mapping.');
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('AI mapping request cancelled by user.');
        showNotification('info', 'AI mapping cancelled.');
      } else {
        console.error('Failed to get AI mapping suggestions:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setAiMappingError(errorMessage);

        const hasPartialResults = aiMappingStats && aiMappingStats.valid > 0;

        if (hasPartialResults) {
          showNotification('warning', `AI mapping completed with partial results. Some properties could not be mapped.`);
        } else {
          showNotification('error', `Failed to get AI mapping suggestions: ${errorMessage}`);
        }
      }
    } finally {
      setIsProcessingAI(false);
      setAiProgress(100);
      abortControllerRef.current = null;
    }
  }, [state.allContractProperties, availableMappings, state.normalizedProperties, handleUpdateProperty, showNotification, aiMappingStats]);

  // Handle removing a mapping
  const handleRemoveMapping = useCallback((propertyId: string) => {
    handleUpdateProperty(propertyId, {
      isNormalized: false,
      normalizedPath: undefined,
      transformationType: undefined,
      transformationConfig: undefined,
      matchType: undefined,
      aiCertaintyScore: undefined,
      aiSuggested: undefined,
      matchStatus: undefined,
    });
  }, [handleUpdateProperty]);

  // Handle auto-mapping for exact path matches
  const handleAutoMapExactMatches = useCallback(() => {
    let changesMade = false;
    state.allContractProperties.forEach(property => {
      if (!property.isNormalized) {
        const exactMatch = availableMappings.find(
          mapping => mapping.path === property.path
        );
        if (exactMatch) {
          handleUpdateProperty(property.id, {
            normalizedPath: exactMatch.path,
            isNormalized: true,
            transformationType: 'mapping',
            transformationConfig: { mappingType: 'direct' },
            matchType: undefined,
            aiCertaintyScore: undefined,
            aiSuggested: false
          });
          changesMade = true;
        }
      }
    });
    if (changesMade) {
      showNotification('success', 'Exact matches auto-mapped successfully.');
    } else {
      showNotification('info', 'No exact path matches found for unmapped properties.');
    }
  }, [state.allContractProperties, availableMappings, handleUpdateProperty, showNotification]);

  useEffect(() => {
    if (shouldAutoMap && !isLoadingMappings && availableMappings.length > 0) {
      handleAutoMapExactMatches();
      setShouldAutoMap(false);
    }
  }, [shouldAutoMap, isLoadingMappings, availableMappings, handleAutoMapExactMatches]);

  // Request to delete an event (shows confirmation dialog)
  const handleDeleteEventRequest = useCallback((event: ContractEvent) => {
    setEventToDelete(event);
    setShowConfirmDeleteEventDialog(true);
  }, []);

  // Confirm event deletion
  const confirmDeleteEvent = useCallback(() => {
    if (eventToDelete) {
      const updatedEvents = state.events.filter(event => event.id !== eventToDelete.id);

      const updatedAllContractProperties = state.allContractProperties.filter(
        prop => prop.eventId !== eventToDelete.id
      );

      const updatedNormalizedProperties = { ...state.normalizedProperties };
      updatedAllContractProperties.forEach(prop => {
        if (prop.eventId === eventToDelete.id) {
          delete updatedNormalizedProperties[prop.id];
        }
      });

      updateState({
        events: updatedEvents,
        allContractProperties: updatedAllContractProperties,
        normalizedProperties: updatedNormalizedProperties,
      });

      if (selectedEvent?.id === eventToDelete.id) {
        onEventSelect(updatedEvents.length > 0 ? updatedEvents[0] : null);
      }

      setEventToDelete(null);
      setShowConfirmDeleteEventDialog(false);
      showNotification('success', `Event "${eventToDelete.name}" and its properties deleted.`);
    }
  }, [eventToDelete, state.events, state.allContractProperties, selectedEvent, updateState, showNotification, handleDeleteProperty]);

  // Cancel event deletion
  const cancelDeleteEvent = useCallback(() => {
    setEventToDelete(null);
    setShowConfirmDeleteEventDialog(false);
  }, []);

  // Process uploaded files
  const processUploadedFiles = useCallback(async (files: File[]) => {
    const newEvents: ContractEvent[] = [];
    const newPropertiesFromFiles: ContractPropertyTreeNode[] = [];

    for (const file of files) {
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        try {
          const content = await file.text();
          const parsedContent = JSON.parse(content);

          const { extractSchemaFields } = await import('../../utils/schemaUtils'); // toFriendlyName is already imported
          const [extractedParams] = extractSchemaFields(parsedContent);

          const eventName = toFriendlyName(file.name.replace('.json', ''));
          const eventCodeName = normalizeCodeName(eventName); // Auto-generate codeName
          const eventId = uuidv4();

          const contractProperties: ContractPropertyTreeNode[] = extractedParams.map(param => ({
            id: param.id,
            dataType: param.dataTypes[0] || 'string',
            name: param.codeName,
            path: param.fullPath,
            codeName: param.codeName,
            description: param.description || '',
            isRequired: param.priorities.includes('Required'),
            isPII: param.isPii,
            friendlyName: param.friendlyName,
            isNormalized: false,
            parentPaths: param.parentPaths,
            level: param.level || 0, // Ensure level is always a number
            scope: param.scope,
            eventId: eventId,
            // Initialize ContractPropertyTreeNode specific properties
            children: [],
            hasChildren: false,
            isExpanded: false,
            isLastChild: false,
          }));

          newEvents.push({
            id: eventId,
            name: eventName,
            codeName: eventCodeName, // Add codeName here
            description: '',
            properties: contractProperties,
          });
          newPropertiesFromFiles.push(...contractProperties);

        } catch (error) {
          console.error(`Failed to process file ${file.name}:`, error);
          showNotification('error', `Failed to process file: ${file.name}. Please ensure it's a valid JSON.`);
        }
      } else {
        showNotification('info', `Skipped non-JSON file: ${file.name}`);
      }
    }

    let updatedEvents = [...state.events];
    let updatedAllContractProperties = [...state.allContractProperties];
    let updatedNormalizedProperties = { ...state.normalizedProperties };

    newEvents.forEach(newEvent => {
      const existingEventIndex = updatedEvents.findIndex(e => normalizeCodeName(e.name) === normalizeCodeName(newEvent.name));
      if (existingEventIndex !== -1) {
        const existingEvent = updatedEvents[existingEventIndex];
        const propertiesToRemoveIds = updatedAllContractProperties
          .filter(prop => prop.eventId === existingEvent.id)
          .map(prop => prop.id);

        updatedAllContractProperties = updatedAllContractProperties.filter(prop => !propertiesToRemoveIds.includes(prop.id));
        propertiesToRemoveIds.forEach(propId => {
          delete updatedNormalizedProperties[propId];
        });

        updatedEvents[existingEventIndex] = newEvent;
        showNotification('info', `Event "${newEvent.name}" re-uploaded and replaced.`);
      } else {
        updatedEvents.push(newEvent);
      }
    });

    newPropertiesFromFiles.forEach(newProp => {
      updatedAllContractProperties.push(newProp);
      updatedNormalizedProperties[newProp.id] = newProp;
    });

    if (newEvents.length > 0) {
      updateState({
        events: updatedEvents,
        allContractProperties: updatedAllContractProperties,
        normalizedProperties: updatedNormalizedProperties,
      });
      showNotification('success', `Successfully loaded ${newEvents.length} new event(s).`);
      setShouldAutoMap(true);
    } else {
      showNotification('info', 'No new events were loaded from the uploaded files.');
    }
  }, [updateState, showNotification, state.events, state.allContractProperties, state.normalizedProperties]);

  // Handle file upload (from drop or file input)
  const handleFilesUpload = useCallback(async (files: File[]) => {
    await processUploadedFiles(files);
  }, [processUploadedFiles]);

  // Handle event name/codeName change
  const handleEventChange = useCallback((eventId: string, field: 'name' | 'codeName', value: string) => {
    const updatedEvents = state.events.map((event: ContractEvent) => {
      if (event.id === eventId) {
        let updatedEvent = { ...event, [field]: value };

        // Logic for auto-generating codeName
        if (field === 'name') {
          // Only auto-generate codeName if it has not been manually edited
          if (!isEventCodeNameManuallyEdited) {
            updatedEvent.codeName = normalizeCodeName(value);
          }
        } else if (field === 'codeName') {
          // If codeName is explicitly changed, mark as manually edited
          setIsEventCodeNameManuallyEdited(true);
          // Add validation for codeName
          if (value.trim() === '') {
            setEventCodeNameError("Code Name can't be empty");
          } else if (!isValidCodeName(value)) {
            setEventCodeNameError('Must contain only letters, numbers, underscores, hyphens, or spaces');
          } else {
            setEventCodeNameError(null);
          }
        }
        return updatedEvent;
      }
      return event;
    });

    // Update selected event if the modified event was selected
    if (selectedEvent && selectedEvent.id === eventId) {
      const updatedSelected = updatedEvents.find(e => e.id === eventId);
      if (updatedSelected) {
        onEventSelect(updatedSelected);
      }
    }

    updateState({ events: updatedEvents });
  }, [state.events, updateState, isEventCodeNameManuallyEdited, selectedEvent]);

  // Determine if the contract is editable (similar to SchemaEditPage)
  const isEditable = state.status === 'draft';

  return (
    <ContractNormalizationLayout
      isLoading={isLoadingMappings}
    >
      {/* Left Panel - Raw Data Event Definition (ContractEventsPanel) */}
      <ContractEventsPanel
        state={state}
        updateState={updateState}
        selectedEvent={selectedEvent}
        onEventSelect={onEventSelect}
        isEditable={isEditable}
        onFilesUpload={handleFilesUpload}
        onDeleteEventRequest={handleDeleteEventRequest}
        isSmallScreen={isSmallScreen}
      />
      {/* Right Panel - Data Normalization (NormalizationPropertiesPanel) */}
      <NormalizationPropertiesPanel
        state={state}
        updateState={updateState}
        selectedEvent={selectedEvent || null}
        availableMappings={availableMappings}
        isLoadingMappings={isLoadingMappings}
        handleAIMappingSuggestion={handleAIMappingSuggestion}
        handleAIMappingBatch={handleAIMappingBatch}
        handleCancelAIMapping={() => {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            console.log('AI mapping request aborted via cancel button in properties panel.');
            showNotification('info', 'AI mapping cancelled.');
          }
        }}
        handleAutoMapExactMatches={handleAutoMapExactMatches}
        handleRemoveMapping={handleRemoveMapping}
        isProcessingAI={isProcessingAI}
        className="w-full md:w-2/3 flex-shrink-0"
        handleAddProperty={handleAddProperty}
        handleUpdateProperty={handleUpdateProperty}
        handleDeleteProperty={handleDeleteProperty}
        isEventNameEditing={isEventNameEditing}
        setIsEventNameEditing={setIsEventNameEditing}
        isEventCodeNameEditing={isEventCodeNameEditing}
        setIsEventCodeNameEditing={setIsEventCodeNameEditing}
        eventCodeNameError={eventCodeNameError}
        eventNameInputRef={eventNameInputRef}
        eventCodeNameInputRef={eventCodeNameInputRef}
        handleEventChange={handleEventChange}
      />
      <AIMappingProgressDialog
        isOpen={showAIMappingProgressDialog}
        progress={aiProgress}
        progressDetail={aiProgressDetail}
        isComplete={aiProgress >= 100 && !isProcessingAI}
        stats={aiMappingStats}
        error={aiMappingError}
        hasPartialSuccess={aiMappingStats ? (aiMappingStats.matched > 0 && aiMappingStats.matched < aiMappingStats.total) : false}
        onClose={() => {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            console.log('AI mapping request aborted via dialog close.');
            showNotification('info', 'AI mapping cancelled.');
          }
          setShowAIMappingProgressDialog(false);
        }}
        onCancel={() => {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            console.log('AI mapping request aborted via cancel button.');
            showNotification('info', 'AI mapping cancelled.');
          }
          setShowAIMappingProgressDialog(false);
        }}
        onRetry={handleAIMappingBatch}
      />
      {showConfirmDeleteEventDialog && (
        <ConfirmDialog
          onClose={() => setShowConfirmDeleteEventDialog(false)}
          onConfirm={confirmDeleteEvent}
          title="Delete Event"
          message={`Are you sure you want to delete the event "${eventToDelete?.name}"? This action cannot be undone.`}
          cancelAction={cancelDeleteEvent}
          cancelText="Cancel"
          confirmText="Delete"
          isDanger={true}
        />
      )}
    </ContractNormalizationLayout>
  );
};

export default DataNormalizationTab;
